import { createSlice, Slice } from '@reduxjs/toolkit';
import { IPaginatedData } from 'modules/shared/interfaces/paginationMeta';
import { IReqStatus } from 'modules/shared/interfaces/requestStatus';
import { RootStateOrAny } from 'react-redux';
import { ISceneCard } from '../interfaces/scene-card';
import { ISceneTag } from '../interfaces/scene-tag';
import { IScenesGroup } from '../interfaces/scenes-group';

export const SCENE_MATCHING_KEY_IN_STORE = 'scene-matching';

export interface ISceneMatchingState {
	isLoading: boolean;
	sceneTags: IPaginatedData<ISceneTag>;
	deleteSceneTag: IReqStatus;
	addSceneTag: IReqStatus;
	searchSceneTagsAutoComplete: {
		isLoading: boolean;
		result: ISceneTag[];
	};
	updateSceneTag: {
		itemToUpdate: {
			isLoading: boolean;
			data?: ISceneTag;
			status: IReqStatus;
		};
		status: IReqStatus;
	};

	// ---

	sceneCards: IPaginatedData<ISceneCard>;
	deleteSceneCard: IReqStatus;
	addSceneCard: IReqStatus;
	searchSceneCardsAutoComplete: {
		isLoading: boolean;
		result: ISceneCard[];
	};
	updateSceneCard: {
		itemToUpdate: {
			isLoading: boolean;
			data?: ISceneCard;
			status: IReqStatus;
		};
		status: IReqStatus;
	};

	// --

	scenesGroups: IPaginatedData<IScenesGroup>;
	deleteScenesGroup: IReqStatus;
	addScenesGroup: IReqStatus;
	searchScenesGroupsAutoComplete: {
		isLoading: boolean;
		result: IScenesGroup[];
	};
	updateScenesGroup: {
		itemToUpdate: {
			isLoading: boolean;
			data?: IScenesGroup;
			status: IReqStatus;
		};
		status: IReqStatus;
	};
}

const initialState: ISceneMatchingState = {
	isLoading: false,
	sceneTags: {
		items: [],
		meta: {
			currentPage: 1,
			itemsPerPage: 5,
			itemCount: 2,
			totalItems: 2,
			totalPages: 1,
		},
	},
	searchSceneTagsAutoComplete: {
		isLoading: false,
		result: [],
	},
	addSceneTag: {
		success: false,
		error: false,
	},
	deleteSceneTag: {
		success: false,
		error: false,
	},
	updateSceneTag: {
		itemToUpdate: {
			isLoading: false,
			data: undefined,
			status: {
				success: false,
				error: false,
			},
		},
		status: {
			success: false,
			error: false,
		},
	},

	// ---

	sceneCards: {
		items: [],
		meta: {
			currentPage: 1,
			itemsPerPage: 5,
			itemCount: 2,
			totalItems: 2,
			totalPages: 1,
		},
	},
	searchSceneCardsAutoComplete: {
		isLoading: false,
		result: [],
	},
	addSceneCard: {
		success: false,
		error: false,
	},
	deleteSceneCard: {
		success: false,
		error: false,
	},
	updateSceneCard: {
		itemToUpdate: {
			isLoading: false,
			data: undefined,
			status: {
				success: false,
				error: false,
			},
		},
		status: {
			success: false,
			error: false,
		},
	},

	// ---

	scenesGroups: {
		items: [],
		meta: {
			currentPage: 1,
			itemsPerPage: 5,
			itemCount: 2,
			totalItems: 2,
			totalPages: 1,
		},
	},
	searchScenesGroupsAutoComplete: {
		isLoading: false,
		result: [],
	},
	addScenesGroup: {
		success: false,
		error: false,
	},
	deleteScenesGroup: {
		success: false,
		error: false,
	},
	updateScenesGroup: {
		itemToUpdate: {
			isLoading: false,
			data: undefined,
			status: {
				success: false,
				error: false,
			},
		},
		status: {
			success: false,
			error: false,
		},
	},
};

const sceneMatchingSlice: Slice = createSlice({
	name: SCENE_MATCHING_KEY_IN_STORE,
	initialState,
	reducers: {
		resetSceneMatchingData: () => {
			return initialState;
		},
		getSceneTags: state => {
			state.isLoading = true;
		},
		getSceneTagsSuccess: (state, action) => {
			state.sceneTags = action.payload;
			state.isLoading = false;
		},
		getSceneTagsFailure: state => {
			state.isLoading = false;
		},
		deleteSceneTag: state => {
			state.deleteSceneTag = {
				success: false,
				error: false,
			};
			state.isLoading = true;
		},
		deleteSceneTagSuccess: (state: RootStateOrAny) => {
			state.deleteSceneTag = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		deleteSceneTagFailure: (state: RootStateOrAny) => {
			state.deleteSceneTag = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},
		resetDeleteSceneTagStatus: state => {
			state.deleteSceneTag = {
				success: false,
				error: false,
			};
		},
		addSceneTag: state => {
			state.isLoading = true;
			state.addSceneTag = {
				success: false,
				error: false,
			};
		},
		addSceneTagSuccess: (state: RootStateOrAny) => {
			state.addSceneTag = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		addSceneTagFailure: state => {
			state.addSceneTag = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},
		getSceneTagToUpdate: state => {
			state.updateSceneTag.itemToUpdate = {
				isLoading: true,
				data: undefined,
				status: {
					success: false,
					error: false,
				},
			};
		},
		getSceneTagToUpdateSuccess: (state, action) => {
			state.updateSceneTag.itemToUpdate = {
				isLoading: false,
				data: action.payload,
				status: {
					success: true,
					error: false,
				},
			};
		},
		getSceneTagToUpdateFailure: state => {
			state.updateSceneTag.itemToUpdate = {
				isLoading: false,
				data: undefined,
				status: {
					success: false,
					error: true,
				},
			};
		},
		updateSceneTag: state => {
			state.isLoading = true;
			state.updateSceneTag.status = {
				success: false,
				error: false,
			};
		},
		updateSceneTagSuccess: (state, action) => {
			const updatedSceneTag = action.payload;
			state.isLoading = false;
			state.sceneTags.items = state.sceneTags.items.map(sceneTag =>
				sceneTag.id === updatedSceneTag.id ? updatedSceneTag : sceneTag,
			);
			state.updateSceneTag.status = {
				success: true,
				error: false,
			};
		},
		updateSceneTagFailure: state => {
			state.isLoading = false;
			state.updateSceneTag.status = {
				success: false,
				error: true,
			};
		},

		searchSceneTags: state => {
			state.searchSceneTagsAutoComplete = {
				isLoading: true,
				result: [],
			};
		},
		setSearchSceneTagsAutoCompleteResult: (state, action) => {
			state.searchSceneTagsAutoComplete = {
				isLoading: false,
				result: action.payload,
			};
		},
		resetSetSearchSceneTagsAutoComplete: state => {
			state.searchSceneTagsAutoComplete = {
				isLoading: false,
				result: [],
			};
		},

		// ---

		getSceneCards: state => {
			state.isLoading = true;
		},
		getSceneCardsSuccess: (state, action) => {
			state.sceneCards = action.payload;
			state.isLoading = false;
		},
		getSceneCardsFailure: state => {
			state.isLoading = false;
		},
		deleteSceneCard: state => {
			state.deleteSceneCard = {
				success: false,
				error: false,
			};
			state.isLoading = true;
		},
		deleteSceneCardSuccess: (state: RootStateOrAny) => {
			state.deleteSceneCard = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		deleteSceneCardFailure: (state: RootStateOrAny) => {
			state.deleteSceneCard = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},
		resetDeleteSceneCardStatus: state => {
			state.deleteSceneCard = {
				success: false,
				error: false,
			};
		},
		addSceneCard: state => {
			state.isLoading = true;
			state.addSceneCard = {
				success: false,
				error: false,
			};
		},
		addSceneCardSuccess: (state: RootStateOrAny) => {
			state.addSceneCard = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		addSceneCardFailure: state => {
			state.addSceneCard = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},
		getSceneCardToUpdate: state => {
			state.updateSceneCard.itemToUpdate = {
				isLoading: true,
				data: undefined,
				status: {
					success: false,
					error: false,
				},
			};
		},
		getSceneCardToUpdateSuccess: (state, action) => {
			state.updateSceneCard.itemToUpdate = {
				isLoading: false,
				data: action.payload,
				status: {
					success: true,
					error: false,
				},
			};
		},
		getSceneCardToUpdateFailure: state => {
			state.updateSceneCard.itemToUpdate = {
				isLoading: false,
				data: undefined,
				status: {
					success: false,
					error: true,
				},
			};
		},
		updateSceneCard: state => {
			state.isLoading = true;
			state.updateSceneCard.status = {
				success: false,
				error: false,
			};
		},
		updateSceneCardSuccess: (state, action) => {
			const updatedSceneCard = action.payload;
			state.isLoading = false;
			state.sceneCards.items = state.sceneCards.items.map(sceneCard =>
				sceneCard.id === updatedSceneCard.id ? updatedSceneCard : sceneCard,
			);
			state.updateSceneCard.status = {
				success: true,
				error: false,
			};
		},
		updateSceneCardFailure: state => {
			state.isLoading = false;
			state.updateSceneCard.status = {
				success: false,
				error: true,
			};
		},

		searchSceneCards: state => {
			state.searchSceneCardsAutoComplete = {
				isLoading: true,
				result: [],
			};
		},
		setSearchSceneCardsAutoCompleteResult: (state, action) => {
			state.searchSceneCardsAutoComplete = {
				isLoading: false,
				result: action.payload,
			};
		},
		resetSetSearchSceneCardsAutoComplete: state => {
			state.searchSceneCardsAutoComplete = {
				isLoading: false,
				result: [],
			};
		},

		// --

		getScenesGroups: state => {
			state.isLoading = true;
		},
		getScenesGroupsSuccess: (state, action) => {
			state.scenesGroups = action.payload;
			state.isLoading = false;
		},
		getScenesGroupsFailure: state => {
			state.isLoading = false;
		},
		deleteScenesGroup: state => {
			state.deleteScenesGroup = {
				success: false,
				error: false,
			};
			state.isLoading = true;
		},
		deleteScenesGroupSuccess: (state: RootStateOrAny) => {
			state.deleteScenesGroup = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		deleteScenesGroupFailure: (state: RootStateOrAny) => {
			state.deleteScenesGroup = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},
		resetDeleteScenesGroupStatus: state => {
			state.deleteScenesGroup = {
				success: false,
				error: false,
			};
		},
		addScenesGroup: state => {
			state.isLoading = true;
			state.addScenesGroup = {
				success: false,
				error: false,
			};
		},
		addScenesGroupSuccess: (state: RootStateOrAny) => {
			state.addScenesGroup = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		addScenesGroupFailure: state => {
			state.addScenesGroup = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},
		getScenesGroupToUpdate: state => {
			state.updateScenesGroup.itemToUpdate = {
				isLoading: true,
				data: undefined,
				status: {
					success: false,
					error: false,
				},
			};
		},
		getScenesGroupToUpdateSuccess: (state, action) => {
			state.updateScenesGroup.itemToUpdate = {
				isLoading: false,
				data: action.payload,
				status: {
					success: true,
					error: false,
				},
			};
		},
		getScenesGroupToUpdateFailure: state => {
			state.updateScenesGroup.itemToUpdate = {
				isLoading: false,
				data: undefined,
				status: {
					success: false,
					error: true,
				},
			};
		},
		updateScenesGroup: state => {
			state.isLoading = true;
			state.updateScenesGroup.status = {
				success: false,
				error: false,
			};
		},
		updateScenesGroupSuccess: (state, action) => {
			const updatedScenesGroup = action.payload;
			state.isLoading = false;
			state.scenesGroups.items = state.scenesGroups.items.map(scenesGroup =>
				scenesGroup.id === updatedScenesGroup.id
					? updatedScenesGroup
					: scenesGroup,
			);
			state.updateScenesGroup.status = {
				success: true,
				error: false,
			};
		},
		updateScenesGroupFailure: state => {
			state.isLoading = false;
			state.updateScenesGroup.status = {
				success: false,
				error: true,
			};
		},

		searchScenesGroups: state => {
			state.searchScenesGroupsAutoComplete = {
				isLoading: true,
				result: [],
			};
		},
		setSearchScenesGroupsAutoCompleteResult: (state, action) => {
			state.searchScenesGroupsAutoComplete = {
				isLoading: false,
				result: action.payload,
			};
		},
		resetSetSearchScenesGroupsAutoComplete: state => {
			state.searchScenesGroupsAutoComplete = {
				isLoading: false,
				result: [],
			};
		},
	},
});

export const {
	resetSceneMatchingData,

	getSceneTags,
	getSceneTagsSuccess,
	getSceneTagsFailure,

	deleteSceneTag,
	deleteSceneTagSuccess,
	deleteSceneTagFailure,

	resetDeleteSceneTagStatus,
	addSceneTag,
	addSceneTagSuccess,
	addSceneTagFailure,

	getSceneTagToUpdate,
	getSceneTagToUpdateSuccess,
	getSceneTagToUpdateFailure,

	updateSceneTag,
	updateSceneTagSuccess,
	updateSceneTagFailure,

	searchSceneTags,
	setSearchSceneTagsAutoCompleteResult,
	resetSetSearchSceneTagsAutoComplete,

	// ---

	getSceneCards,
	getSceneCardsSuccess,
	getSceneCardsFailure,

	deleteSceneCard,
	deleteSceneCardSuccess,
	deleteSceneCardFailure,

	resetDeleteSceneCardStatus,
	addSceneCard,
	addSceneCardSuccess,
	addSceneCardFailure,

	getSceneCardToUpdate,
	getSceneCardToUpdateSuccess,
	getSceneCardToUpdateFailure,

	updateSceneCard,
	updateSceneCardSuccess,
	updateSceneCardFailure,

	searchSceneCards,
	setSearchSceneCardsAutoCompleteResult,
	resetSetSearchSceneCardsAutoComplete,

	// ---

	getScenesGroups,
	getScenesGroupsSuccess,
	getScenesGroupsFailure,

	deleteScenesGroup,
	deleteScenesGroupSuccess,
	deleteScenesGroupFailure,
	resetDeleteScenesGroupStatus,

	addScenesGroup,
	addScenesGroupSuccess,
	addScenesGroupFailure,

	getScenesGroupToUpdate,
	getScenesGroupToUpdateSuccess,
	getScenesGroupToUpdateFailure,

	updateScenesGroup,
	updateScenesGroupSuccess,
	updateScenesGroupFailure,

	searchScenesGroups,
	setSearchScenesGroupsAutoCompleteResult,
	resetSetSearchScenesGroupsAutoComplete,
} = sceneMatchingSlice.actions;

export const sceneMatchingStateSelector = (
	state: RootStateOrAny,
): ISceneMatchingState => state[SCENE_MATCHING_KEY_IN_STORE];

export default sceneMatchingSlice;
